import { Brin6Schema, UuidSchema } from "@spark/domain/schemas";
import * as z from "zod";
import { deepFreeze } from "@spark/domain";

//#region src/features.ts
const FEATURE_SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
globalThis.__sparkFeatures ??= {
	schemas: /* @__PURE__ */ new Map(),
	values: /* @__PURE__ */ new Map(),
	resolved: /* @__PURE__ */ new Map()
};
const state = globalThis.__sparkFeatures;
function resolveConfig(feature, schema, value) {
	if (value === void 0 || value === false) return false;
	const result = schema.safeParse(value === true ? {} : value);
	if (!result.success) {
		console.warn(`Warning: invalid configuration for feature '${feature}'. Disabling.`);
		return false;
	}
	return deepFreeze(result.data);
}
function registerFeature(feature, schema) {
	if (!FEATURE_SLUG.test(feature)) throw new Error(`Invalid feature slug: '${feature}'.`);
	const config = resolveConfig(feature, schema, state.values.get(feature));
	if (state.schemas.has(feature) && JSON.stringify(state.resolved.get(feature)) !== JSON.stringify(config)) console.warn(`Warning: feature '${feature}' registered multiple times.`);
	state.schemas.set(feature, schema);
	state.resolved.set(feature, config);
}
function configureFeature(feature, value) {
	setFeatureValue(feature, value);
}
/**
* Configure multiple features at once; see {@link configureFeature}.
*
* @param features Feature slug -> `true`, `false`, or configuration object.
*/
function configureFeatures(features) {
	for (const [feature, value] of Object.entries(features)) setFeatureValue(feature, value);
}
function setFeatureValue(feature, value) {
	if (!FEATURE_SLUG.test(feature)) {
		console.warn(`Warning: invalid feature slug '${feature}'. Ignoring.`);
		return;
	}
	state.values.set(feature, value);
	const schema = state.schemas.get(feature);
	if (schema) state.resolved.set(feature, resolveConfig(feature, schema, value));
}
/**
* @returns Slugs of all registered features enabled in this environment.
*/
function enabledFeatures() {
	return [...state.resolved].filter(([, config]) => config !== false).map(([feature]) => feature).toSorted();
}
/**
* @returns Slugs of all registered features.
*/
function registeredFeatures() {
	return [...state.schemas.keys()].toSorted();
}
/**
* @param feature Feature slug.
* @returns Whether the feature is registered and enabled with a valid
*   configuration.
*/
function isFeatureEnabled(feature) {
	return Boolean(state.resolved.get(feature));
}
/**
* @param feature Feature slug.
* @returns The feature's configuration parsed with its registered schema, or
*   `false` if the feature is disabled, unregistered, or misconfigured.
*/
function getFeatureConfig(feature) {
	return state.resolved.get(feature) ?? false;
}

//#endregion
//#region src/shared-features.ts
const BlinkLezenSchema = z.object({
	schools: z.union([z.literal("*"), z.array(Brin6Schema)]).default("*"),
	users: z.union([z.literal("*"), z.array(UuidSchema)]).default("*")
});
const OnlineModulesSchema = z.object({});
const BlinkEngelsSchema = z.object({
	schools: z.union([z.literal("*"), z.array(Brin6Schema)]).default("*"),
	users: z.union([z.literal("*"), z.array(UuidSchema)]).default("*")
});
registerFeature("blink-lezen", BlinkLezenSchema);
registerFeature("blink-engels", BlinkEngelsSchema);
registerFeature("online-modules", OnlineModulesSchema);

//#endregion
export { configureFeature, configureFeatures, enabledFeatures, getFeatureConfig, isFeatureEnabled, registerFeature, registeredFeatures };